import { useMutation, useQuery } from "@tanstack/react-query";
import { crfApiService } from "modules/Library/CRF/api/crf.api";

interface ApiResponse<T = any> {
  data: T;
  message: string;
  statuscode: number;
}

// ------------------------------------------------------------------
//-- CRF
// ------------------------------------------------------------------ 

export const useCRFService = ({
  otherid,
  moduleid,
  modulename,
  categorynames,
  searchText,
  pageNumber,
  pageSize,
  id,
  crfcategory,
  ispagination,
  enabledSelectAll,
  enabledAssignCRFSelectAll,
  enabledCategorySelectAll,
}: {
  otherid?: number | null;
  moduleid?: number | string | null;
  modulename?: string|null;
  categorynames?: string[] | null;
  searchText?: string | null;
  pageNumber?: number;
  pageSize?: number;
  id?: number;
  crfcategory?: number | string | null;
  ispagination?: boolean;
  enabledSelectAll?: boolean;
  enabledAssignCRFSelectAll?: boolean;
  enabledCategorySelectAll?: boolean;
} = {}) => {

  const SelectAllQuery = useQuery({
    queryKey: ["crf-selectAll", otherid, searchText, pageNumber, pageSize],
    queryFn: () => crfApiService.getSelectAllData(otherid, searchText, pageNumber, pageSize),
    enabled: (enabledSelectAll !== undefined ? enabledSelectAll : true) && !!pageNumber && !!pageSize,
  });

  const AssignCRFSelectAllQuery = useQuery({
    queryKey: ["crf-assignment-selectAll", moduleid, modulename, categorynames, searchText, pageNumber, pageSize],
    queryFn: () =>
      crfApiService.assignCRFSelectAll({
        moduleid: moduleid !== undefined ? moduleid : null,
        modulename: modulename !== undefined ? modulename : null,
        categorynames: categorynames !== undefined ? categorynames : null,
        searchtext: searchText,
        currentpage: pageNumber,
        pagesize: pageSize,
      }),
    enabled: (enabledAssignCRFSelectAll !== undefined ? enabledAssignCRFSelectAll : true) && (!!moduleid || !!modulename || !!categorynames),
  });

  const SelectQuery = useQuery({
    queryKey: ["crf-select", id],
    queryFn: () => crfApiService.getSelectData(id!),
    enabled: !!id,
  });

  const InsertMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) =>
      crfApiService.insertData(payload),
  });

  const UpdateMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) =>
      crfApiService.updateData(payload),
  });

  const DeleteMutation = useMutation<ApiResponse, Error, { crfid: number | null, updatereason: string }>({
    mutationFn: (payload) =>
      crfApiService.deleteData({ crfid: payload.crfid, updatereason: payload.updatereason }),
  });
  const DropdownMutation = useMutation<ApiResponse, Error, void>({
    mutationFn: () =>
      crfApiService.getDropdownData()
  });

  const AssignCRFInsertMutation = useMutation<ApiResponse, Error, { moduleid: number | string; crfids: (number | string)[] }>({
    mutationFn: (payload) => crfApiService.assignCRFInsert(payload),
  });

  const AssignCRFDeleteMutation = useMutation<ApiResponse, Error, { assignmentid?: number | string; updatereason?: string }>({
    mutationFn: (payload) => crfApiService.assignCRFDelete(payload),
  });

  const UpdateCRFCategoryMutation = useMutation<ApiResponse, Error, { moduleid: number | string; crfcategory: number | string; }>({
    mutationFn: (payload) => crfApiService.updateCRFModuleCategory(payload),
  });

  const CategorySelectAllQuery = useQuery({
    queryKey: ["crf-category-selectAll", searchText, pageNumber, pageSize, crfcategory, ispagination],
    queryFn: () =>
      crfApiService.assignCRFCategorySelectAll({
        searchtext: searchText,
        currentpage: pageNumber,
        pagesize: pageSize,
        crfcategory: crfcategory || null,
        ispagination: ispagination,
      }),
    enabled: enabledCategorySelectAll !== undefined ? enabledCategorySelectAll : true,
  });

  return {
    SelectAllQuery,
    AssignCRFSelectAllQuery,
    SelectQuery,
    InsertMutation,
    UpdateMutation,
    DeleteMutation,
    DropdownMutation,
    AssignCRFInsertMutation,
    AssignCRFDeleteMutation,
    UpdateCRFCategoryMutation,
    CategorySelectAllQuery,
  };
};